import { ApiRequestError } from '../types/common';
import apiClient from './apiClient';

export type TipoDePergunta = 'simnao' | 'texto' | 'opcoes';

export interface Pergunta {
  id: string;
  texto: string;
  tipo: TipoDePergunta;
  detalhe?: string;
  opcoes?: Array<{ valor: string; rotulo: string }>;
}

export interface Secao {
  id: string;
  titulo: string;
  perguntas: Pergunta[];
}

export interface Resposta {
  valor: string;
  detalhe?: string;
}

export type Respostas = Record<string, Resposta>;

export type StatusDaAnamnese = 'nao_enviada' | 'pendente' | 'respondida' | 'cancelada';

export interface SituacaoDaAnamnese {
  status: StatusDaAnamnese;
  enviadaEm: string | null;
  canalDoEnvio: string | null;
  respondidaEm: string | null;
  origemDaResposta: 'link' | 'tablet' | null;
}

/** Os campos do formulário de anamnese do médico que as respostas sugerem. */
export interface SugestaoParaAnamnese {
  chiefComplaint: string | null;
  medicalHistory: string | null;
  currentMedications: string | null;
  allergies: string | null;
  previousJobs: string | null;
  workAccidentHistory: string | null;
  occupationalHistory: string | null;
  familyHistory: string | null;
  smokingStatus: string | null;
  alcoholUse: string | null;
  physicalActivity: boolean | null;
  physicalActivityDetails: string | null;
}

export interface RespostasParaOMedico extends SituacaoDaAnamnese {
  versaoDoQuestionario: string | null;
  questionario: Secao[];
  respostas: Respostas;
  sugestao: SugestaoParaAnamnese | null;
  temAssinatura: boolean;
}

export interface ResumoPublico {
  status: 'pendente' | 'respondida' | 'cancelada' | 'expirada';
  trabalhador: string;
  empresa: string | null;
  dataDoExame: string;
}

export interface QuestionarioPublico {
  questionario: Secao[];
  declaracao: string;
}

/** O erro do servidor em texto para a pessoa, sem "Request failed with status code". */
function mensagemDoErro(err: unknown, padrao: string): Error {
  if (err instanceof ApiRequestError && err.message && !/status code/i.test(err.message)) return new Error(err.message);
  if (err instanceof ApiRequestError && err.status === 404) return new Error('Link inválido ou já utilizado.');
  return new Error(padrao);
}

async function chamar<T>(fn: () => Promise<unknown>, padrao: string): Promise<T> {
  try {
    return (await fn()) as T;
  } catch (err: unknown) {
    throw mensagemDoErro(err, padrao);
  }
}

export const anamneseDigitalService = {
  situacao(appointmentId: number | string): Promise<SituacaoDaAnamnese> {
    return chamar(() => apiClient.get(`/anamnese-digital/agendamento/${appointmentId}/situacao`), 'Não foi possível ver a anamnese.');
  },

  respostas(appointmentId: number | string): Promise<RespostasParaOMedico> {
    return chamar(() => apiClient.get(`/anamnese-digital/agendamento/${appointmentId}/respostas`), 'Não foi possível carregar a anamnese.');
  },

  gerarLink(appointmentId: number | string, enviar: boolean): Promise<{ url: string; enviadoPor: string | null }> {
    return chamar(() => apiClient.post(`/anamnese-digital/agendamento/${appointmentId}/link`, { enviar }), 'Não foi possível gerar o link.');
  },

  resumoPublico(token: string): Promise<ResumoPublico> {
    return chamar(() => apiClient.get(`/anamnese-publica/${encodeURIComponent(token)}`), 'Não foi possível abrir o questionário.');
  },

  confirmarCpf(token: string, cpf: string): Promise<QuestionarioPublico> {
    return chamar(() => apiClient.post(`/anamnese-publica/${encodeURIComponent(token)}/cpf`, { cpf }), 'Não foi possível conferir o CPF.');
  },

  responder(
    token: string,
    dados: { cpf: string; respostas: Respostas; aceite: boolean; imagem: string; origem: 'link' | 'tablet' }
  ): Promise<{ status: 'respondida' }> {
    return chamar(() => apiClient.post(`/anamnese-publica/${encodeURIComponent(token)}/responder`, dados), 'Não foi possível enviar o questionário.');
  },
};

export default anamneseDigitalService;
